import { supabase } from "@/integrations/supabase/client";
import { v4 as uuidv4 } from "uuid";

/**
 * Uploads an image to Supabase storage and returns the public URL
 */
export const uploadStoryImage = async (file: File): Promise<string> => {
  if (!file) throw new Error("No file provided");
  
  try {
    // Create a unique file name
    const fileExt = file.name.split('.').pop();
    const fileName = `${uuidv4()}.${fileExt}`;
    const filePath = `${fileName}`; // Uploaded directly to the bucket root

    console.log("Attempting to upload:", { fileName, fileExt, filePath });

    // Upload file to Supabase Storage
    const { data, error } = await supabase.storage
      .from('stories')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true // Changed to true to overwrite if file exists
      });

    if (error) {
      console.error("Upload error:", error);
      throw new Error(`Upload failed: ${error.message}`);
    }

    console.log("Upload successful:", data);

    // Get the public URL for the uploaded file
    const { data: { publicUrl } } = supabase.storage
      .from('stories')
      .getPublicUrl(filePath);
    
    console.log("Generated public URL:", publicUrl);
    return publicUrl;
  } catch (error) {
    console.error("Error in uploadStoryImage:", error);
    throw error;
  }
};

/**
 * Gets the appropriate image path for preview or display
 * Enhanced to provide more reliable image handling with improved logging
 */
export const getImagePreviewPath = (storyImage?: string): string => {
  console.log("Getting image preview for:", storyImage);
  
  if (!storyImage || storyImage === "undefined" || storyImage === "null") {
    console.log("No valid image provided, using placeholder");
    return "/placeholder.svg";
  }
  
  // If it's a Supabase storage URL, return it directly
  if (storyImage.includes("storage.googleapis.com") || storyImage.includes("supabase")) {
    console.log("Using Supabase URL for preview:", storyImage);
    return storyImage;
  }
  
  // Check if it's an absolute URL (includes http:// or https://)
  if (storyImage.startsWith('http')) {
    console.log("Using absolute URL for preview:", storyImage);
    return storyImage;
  }
  
  // If we have a working local image in lovable-uploads, use that
  if (storyImage && storyImage.includes("lovable-uploads")) {
    const path = storyImage.startsWith('/') ? storyImage : `/${storyImage}`;
    console.log("Using local lovable-uploads path:", path);
    return path;
  }
  
  // For local paths that don't start with a slash, add one
  if (storyImage && !storyImage.startsWith('/') && !storyImage.includes("lovable-uploads")) {
    console.log("Adding leading slash to local path");
    return `/${storyImage}`;
  }
  
  // For numeric IDs, map to known working lovable-uploads images
  const idMatch = storyImage.match(/\/(\d+)\./) || storyImage.match(/^(\d+)$/);
  if (idMatch) {
    const numericId = parseInt(idMatch[1]);
    if (!isNaN(numericId)) {
      let fallbackPath;
      switch (numericId) {
        case 1: 
          fallbackPath = "/lovable-uploads/d2cfe4d7-2bad-4598-aced-11e4ac3e36d2.png"; // Solar panel image
          break;
        case 2:
          fallbackPath = "/lovable-uploads/5345efbc-ab4d-4769-a9ac-d2d94af7d692.png"; // Farming image
          break;
        case 3:
          fallbackPath = "/lovable-uploads/7e3bb041-335c-45a2-a9ec-92bbc276ca44.png"; // Updated startup image
          break;
        case 4:
          fallbackPath = "/lovable-uploads/d6a911f7-e55a-4331-a528-f6d5dcc6ae41.png"; // Water image
          break;
        case 5:
          fallbackPath = "/lovable-uploads/1b9e0703-9313-4b25-a35e-76332d449bbe.png"; // Women image
          break;
        default:
          fallbackPath = "/lovable-uploads/0bed4794-3e56-4a50-9f99-773ee667b61c.png"; // Default image
      }
      console.log(`Using fallback image for ID ${numericId}:`, fallbackPath);
      return fallbackPath;
    }
  }
  
  // If none of the above conditions match, return the original path as a last resort
  console.log("Using original path as provided:", storyImage);
  return storyImage;
};
